import {
  DiagnosticMethod,
  IDiagnostic,
  QuestionBankType,
} from "apps/project-iq/models/IDiagnostic";
import { ReactElement } from "react";

export interface ICreateDiagnosticDialogProps {
  isOpen: boolean;
  workspaceId: string;
  onDismiss: () => void;
  onSaved?: () => void;
  mode?: "create" | "edit";
  diagnostic?: IDiagnostic;
}

export type CreateDiagnosticStep =
  | "basic-info"
  | "delivery-method"
  | "template"
  | "question-bank-upload"
  | "context"
  | "messages"
  | "demographics"
  | "branding";

export const STEP_LABELS: Record<CreateDiagnosticStep, string> = {
  "basic-info": "Basic Info",
  "delivery-method": "Method",
  template: "Template",
  "question-bank-upload": "Question Bank",
  context: "Context",
  messages: "Messages",
  demographics: "Demographics",
  branding: "Branding",
};

export const DIALOG_LABELS = {
  TITLE: "New Diagnostic",
  TITLE_EDIT: "Edit Diagnostic",
  NAME_LABEL: "Diagnostic Name",
  DESCRIPTION_LABEL: "Description (optional)",
  METHOD_HEADING: "Select a delivery method",
  METHOD_HELPER:
    "Each diagnostic supports one delivery method. To use multiple delivery methods, create separate diagnostics.",
  SAVING: "Creating",
  SAVING_EDIT: "Saving changes",
  SUBMIT_LABEL: "Create diagnostic",
  SUBMIT_LABEL_EDIT: "Save changes",
  SUBMIT_LABEL_DISABLED: "Create diagnostic (select a delivery method first)",
  SUBMIT_LABEL_EDIT_DISABLED: "Save changes (complete required fields first)",
  BACK: "Back",
  NEXT: "Next",
  NEXT_DISABLED: "Next (complete the current step first)",
  CLOSE: "Close",
  NAME_PLACEHOLDER: "Enter diagnostic name...",
  CONTEXT_PLACEHOLDER: "Describe the engagement, client situation, or topic...",
  LOGO_PREVIEW_ALT: "Logo preview",
  REMOVE_LOGO: "Remove logo",
  HEADER_COLOR_PICKER: "Header color picker",
  HEADER_COLOR_HEX: "Header color hex value",
  ACCENT_COLOR_PICKER: "Accent color picker",
  ACCENT_COLOR_HEX: "Accent color hex value",
  RESET_TO_DEFAULT: "Reset to default",
  NAME_REQUIRED: "Diagnostic Name is required.",
  ERROR_UPDATE: "Failed to update diagnostic. Please try again.",
  ERROR_CREATE: "Failed to create diagnostic. Please try again.",
  CONTEXT_LABEL: "Interview Context",
  ATTACHMENTS_LABEL: "Attachments (optional)",
  CONTEXT_HELPER:
    "Describe the engagement background. This context shapes the AI interview questions.",
  MESSAGES_HELPER:
    "Customize the messages participants see at the start and end of the interview.",
  OPENING_MESSAGE_LABEL: "Opening Message (optional)",
  OPENING_MESSAGE_HINT: "Shown to participants before the interview starts",
  CLOSING_MESSAGE_LABEL: "Closing Message (optional)",
  CLOSING_MESSAGE_HINT:
    "Shown to participants after they complete the interview",
  DEMOGRAPHICS_LABEL: "Demographics",
  DEMOGRAPHICS_HINT:
    "Enable to collect demographic information from interviewees during the AI Interview.",
  DEMOGRAPHICS_HELPER:
    "Collect participant information before or after the interview.",
  CUSTOM_BRANDING_LABEL: "Custom Branding",
  HEADER_LOGO_LABEL: "Header Logo (optional)",
  HEADER_COLOR_LABEL: "Header Color",
  ACCENT_COLOR_LABEL: "Accent Color",
  CUSTOM_BRANDING_HINT:
    "Enable to customize the header color and logo shown in the external interview screens.",
  BRANDING_HELPER:
    "Customize the look of the participant interview experience.",
  QUESTION_BANK_SECTION: "Question bank",
  QUESTION_BANK_SECTION_HELPER:
    "Choose how you'll source questions. This step is only shown for Survey diagnostics.",
  LOADING_TEMPLATES: "Loading templates...",
  TEMPLATE_HEADING: "Pick the NH diagnostic template you want to start from.",
  TEMPLATE_BROWSE_TEXT: "Not sure which one?",
  TEMPLATE_BROWSE_LINK:
    "Browse the full question banks on the NH Diagnostic IQ hub page",
  TEMPLATE_BROWSE_URL:
    "https://northhighland.sharepoint.com/sites/hubProductManagement/SitePages/NH-Diagnostic-IQ.aspx",
  QUESTION_BANK_UPLOAD_HELPER:
    "Upload your question bank as an Excel file. This is optional — you can skip and add it later.",
  QUESTION_BANK_FILE_LABEL: "Question Bank File (optional)",
  QUESTION_BANK_FILE_LABEL_EXISTING: "Question Bank File",
  QUESTION_BANK_MAX_ONE: "Remove the current file to upload a new one",
  ERROR_DOWNLOAD_FILE: "Download failed. Please try again.",
  DOWNLOAD_FILE: "Download",
  ERROR_DELETE_QUESTION_BANK:
    "Failed to remove the previous question bank file. Please try again.",
  REMOVE_FILE: "Remove",
} as const;

export interface IDeliveryMethodOption {
  value: DiagnosticMethod;
  label: string;
  description: string;
  icon: ReactElement;
}

export interface IQuestionBankOption {
  value: QuestionBankType;
  label: string;
  description: string;
}
